import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Form } from 'antd';

import { StepThreeFormContext } from './StepThreeFormContext';
import QuotationService, {
  DEFAULT_PAYMENT_PLAN_CODE,
} from '../../../../../../../../../shared/services/quotation';
import {
  CoveragePerPlan,
  DECISION_TYPE,
  GetCommertialsConditionsResponse,
  HomeQuotationComponent,
  HomeQuotationRequest,
  PropertyFeature,
  QuotationCoverage,
  WebHomeQuotation,
} from '../../../../../../../../../shared/interfaces';
import {
  GenericDataFormContext,
  LoadSavedQuotations,
  PLAN_TYPE,
  QuotationInfoContext,
  StepOneFormContext,
} from '../../../../../shared';
import { StepTwoFormContext } from '../../../StepTwo/contexts/StepTwoForm';
import {
  LOADING_SAVED_QUOTATIONS_STATES,
  QUOTATION_BUTTON,
  QuotationButton,
  QuotationStep,
} from '../../../../../shared';
import {
  generateInputID,
  getCoverageAndActivityKeyByID,
} from '../../../../../AP/Steps/StepThree/components/Quotation/utils';
import { CoveragePlanSelected } from '../../../../interfaces/listPlansSelected';
import { getValueDefaultPaymentMethod } from '../../../../../utils/get-value-default-payment-method';
import { HomeQuotationFormByTab } from '../../components';
import { WizardDataFormContext } from '../../../../Wizard/contexts/WizardDataForm';
import { HomeQuotationFormByTabClosed } from '../../components/HomeFormByTabClosed';
import { LOADING_RENEWAL_QUOTATION_STATES } from '../../../../../shared/interfaces/loading-renewal-quotation-states-enum';
import { scrollToEmitButton } from '../../../../../../../../../shared/utils';

export interface HomeQuotationForm {
  [key: string]: string;
}

export interface HomePaymentMethodForm {
  [key: string]: string | number;
  paymentMethod: string;
  comission: number;
  extraVariable: number;
  variation: number;
}

export interface CoveragePerPlanId {
  [key: string | number]: boolean;
}

export const StepThreeFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const [homePaymentMethodsForm] = Form.useForm<HomePaymentMethodForm>();
  const [stepThreeHomeQuotationForm] = Form.useForm<HomeQuotationForm>();
  const [stepThreeHomeQuotationFormCoverages] = Form.useForm<any>();
  const [stepThreeHomeQuotationFormSummary] = Form.useForm<any>();

  const {
    allLocalitiesPerProvince,
    paymentMethodsList,
    allCoveragesPerPlan,
    setCanShowSpinner,
    setPlansSelected,
    plansSelected,
    updateFormsAfterRemoveTabs,
    formValues,
    removedTabIndexes, 
    setRemovedTabIndexes,
    setTabsUserCanNotDelete
  } = useContext(GenericDataFormContext);
  const { handleTabErrors } = useContext(WizardDataFormContext);

  const { clientSelected } = useContext(StepOneFormContext);
  const {
    quotationInfo,
    currentStep,
    loadSaveQuotationState,
    setLoadSaveQuotationState,
    savedQuotation,
    setCurrentStep,
    quotationToRenewal,
    setRenewalQuotationState,
    renewalQuotationState
  } = useContext(QuotationInfoContext);
  const {
    tabs,
    stepTwoForm,
    allFeatures,
    planTypeSelected,
    setIsPlanTypeADisabled,
    setIsPlanTypeCDisabled, 
  } = useContext(StepTwoFormContext);

  const [canGetCoveragesPerPlanByTab, setCanGetCoveragesPerPlanByTab] =
    useState<CoveragePerPlanId>();
  const [commertialConditions, setCommertialConditions] =
    useState<GetCommertialsConditionsResponse>();
  const [homeQuotationComponents, setHomeQuotationComponents] = useState<
    Array<HomeQuotationComponent>
  >([]);
  const [isLoadingForm, setIsLoadingForm] = useState(false);
  const [listPlansSelected, setListPlansSelected] = useState<
    CoveragePlanSelected[]
  >([]);
  const [planSelectedSummary, setPlanSelectedSummary] = useState<string>('');
  const [quotationResponse, setQuotationResponse] =
    useState<WebHomeQuotation>();
  const [labelButton, setLabelButton] = useState<QuotationButton>(
    quotationResponse ? QUOTATION_BUTTON.RECOTIZAR : QUOTATION_BUTTON.COTIZAR
  );

  const [quotationResponseClosed, setQuotationResponseClosed] = useState<
    Array<{ planSelected: string; response: WebHomeQuotation }>
  >([]);

  const [quotationTabs, setQuotationTabs] = useState<Array<any>>([]);
  const [activeTab, setActiveTab] = useState(1);

  const createTabs = useCallback(() => {
    const newTabs = tabs?.map(({ key, label }: any) => ({
      children:
        planTypeSelected === PLAN_TYPE.OPEN ? (
          <HomeQuotationFormByTab tabKey={key} />
        ) : (
          <HomeQuotationFormByTabClosed tabKey={key} />
        ),
      forceRender: true,
      closable: false,
      key,
      label,
    }));

    setQuotationTabs(newTabs);
  }, [tabs, setQuotationTabs, planTypeSelected]);

  const handlePressTab = (activeTab: any) => setActiveTab(activeTab);

  const clearPrimaFieldsOnFormChange = useCallback(() => {
    const allFields = stepThreeHomeQuotationForm.getFieldsValue();
    const allPrimaKeys = Object.keys(allFields).filter((el) =>
      el.includes('prima')
    );
    stepThreeHomeQuotationForm.resetFields(allPrimaKeys);
  }, [stepThreeHomeQuotationForm]);

  const getCommertialsConditions = useCallback(async () => {
    try {
      const response = await QuotationService.getComertialsConditions(
        quotationInfo?.branch!
      );

      if (response.data) {
        setCommertialConditions(response.data);
      }
    } catch (error) {}
  }, [quotationInfo, setCommertialConditions]);

  const updateFieldsBeforeQuotation = useCallback(
    ({ componentes }: WebHomeQuotation) => {
      const paymentMethodCode = homePaymentMethodsForm.getFieldInstance(
        'paymentMethod'
      )
        ? homePaymentMethodsForm?.getFieldValue('paymentMethod')
        : getValueDefaultPaymentMethod(paymentMethodsList);

      const defaultPaymentMethod = paymentMethodsList?.find(
        (el) => el.codigoFormaDePago === DEFAULT_PAYMENT_PLAN_CODE
      );

      if (
        paymentMethodCode &&
        paymentMethodsList?.find((el) => `${el.codigoFormaDePago}-${el.nroPlanDePago}` === paymentMethodCode)
      ) {
        homePaymentMethodsForm.setFieldValue(
          'paymentMethod',
          paymentMethodCode
        );
      } else {
        homePaymentMethodsForm.setFieldValue(
          'paymentMethod',
          `${defaultPaymentMethod?.codigoFormaDePago}-${defaultPaymentMethod?.nroPlanDePago}`
        );
      }

      if (
        !homePaymentMethodsForm.getFieldValue('commission') &&
        !homePaymentMethodsForm.getFieldValue('extraVariable')
      ) {
        homePaymentMethodsForm.setFieldValue(
          'commission',
          Number(commertialConditions?.porcentajeDeComision)
        );
        homePaymentMethodsForm.setFieldValue(
          'extraVariable',
          Number(commertialConditions?.porcentajeExtraPrimaVariable)
        );
      }

      componentes?.forEach(({ nroDeComponente, coberturas }: any) => {
        coberturas.forEach(({ id, prima }: any) => {
          const primaInputName = generateInputID(
            id,
            `home-tab-${nroDeComponente}`,
            'prima'
          );
          stepThreeHomeQuotationForm.setFieldValue(
            primaInputName,
            Number(prima)
          );
        });
      });
    },
    [
      homePaymentMethodsForm,
      paymentMethodsList,
      commertialConditions,
      stepThreeHomeQuotationForm,
    ]
  );

  const handlerOnClickQuotation = useCallback(
    async (form: HomeQuotationRequest) => {
      setCanShowSpinner(true);

      try {
        await stepThreeHomeQuotationForm.validateFields();
        
        const response = await QuotationService.postGenerateHomeQuotation(form);

        if (response.data) {
          scrollToEmitButton();
          setLabelButton(QUOTATION_BUTTON.EMITIR);

          const tabsUserCanNotDelete: Array<number> = [];

          //recover ID from original coverate and set it into response
          response.data.cotizacionWebHogar.componentes.forEach(
            ({ nroDeComponente, coberturas }) => {
              const planSelected = stepTwoForm!.getFieldValue(
                `plan-tab-${nroDeComponente}`
              );

              tabsUserCanNotDelete.push(Number(nroDeComponente));

              const coveragesPerPlan =
                allCoveragesPerPlan.find(
                  (el) => (el.plan as string) === String(planSelected)
                )?.coverages ?? [];

              coberturas?.forEach((coverage) => {
                const originalCoverage = coveragesPerPlan.find(
                  (item) =>
                    item.codigo === coverage.codigoCobertura &&
                    item.codigoRiesgo === coverage.codigoRiesgo
                );
                coverage.id = originalCoverage?.id;
                coverage.subCategoriaDeTextos = originalCoverage?.subCategoriaDeTextos;
              });
            }
          );

          setTabsUserCanNotDelete(tabsUserCanNotDelete);

          setQuotationResponse(response.data.cotizacionWebHogar);

          updateFieldsBeforeQuotation(response.data.cotizacionWebHogar);

          return;
        }
      } catch (error: any) {
        if (error && error?.errorFields?.length > 0) {
          handleTabErrors(error?.errorFields, ['tab'], 'Hogar');
        }
      } finally {
        setCanShowSpinner(false);
      }
    },
    [
      allCoveragesPerPlan,
      stepThreeHomeQuotationForm,
      setLabelButton,
      setCanShowSpinner,
      setQuotationResponse,
      updateFieldsBeforeQuotation,
      handleTabErrors,
      stepTwoForm,
      setTabsUserCanNotDelete,
    ]
  );

  const generateCoveragesInfo = useCallback(
    (tabKey: string): Array<QuotationCoverage> => {
      const quotationCoverages: Array<QuotationCoverage> = [];

      const allFields = stepThreeHomeQuotationForm.getFieldsValue();

      const allCheckboxFields = Object.keys(allFields).filter((el) =>
        el.includes('checkbox')
      );

      const allCheckboxFieldByTab = allCheckboxFields.filter((el) =>
        el.includes(`home-tab-${tabKey}`)
      );

      allCheckboxFieldByTab.forEach((el) => {
        if (stepThreeHomeQuotationForm.getFieldValue(el)) {
          const { id, activityKey } = getCoverageAndActivityKeyByID(el);

          const planSelected = stepThreeHomeQuotationForm?.getFieldValue(
            `plan-tab-${tabKey}`
          );

          const allCoveragesByTab = allCoveragesPerPlan.find(
            (el) => String(el.plan as string) === String(planSelected)
          )?.coverages;

          const coverage = allCoveragesByTab?.find(
            (el) => String(el.id) === String(id)
          );

          const sumaAseguradaInputName = generateInputID(id, activityKey);

          const primaInputName = generateInputID(id, activityKey, 'prima');

          quotationCoverages.push({
            cobertura: coverage?.codigo!,
            riesgo: coverage?.codigoRiesgo!,
            sumaAsegurada: stepThreeHomeQuotationForm.getFieldValue(
              sumaAseguradaInputName
            ),
            prima:
              stepThreeHomeQuotationForm.getFieldValue(primaInputName) ?? '0',
            pormilaje: '0',
          });
        }
      });

      return quotationCoverages;
    },
    [stepThreeHomeQuotationForm, allCoveragesPerPlan]
  );

  const generateCoveragesInfoByPlan = useCallback(
    (dataComponents: { plan: string; coverages: Array<CoveragePerPlan> }) => {
      const quotationCoverages = dataComponents?.coverages
        .filter((cov) => cov.tipoCobertura === 'B')
        .map((coverage) => ({
          riesgo: coverage.codigoRiesgo,
          cobertura: coverage.codigo,
          sumaAsegurada: coverage.sumaAseguradaPorDefecto,
          pormilaje: '0',
          prima: '0',
        }));

      const allFields = stepThreeHomeQuotationFormCoverages.getFieldsValue();

      const allCheckboxFields = Object.keys(allFields).filter((el) =>
        el.includes(`aditional-plan-${dataComponents.plan}`)
      );

      allCheckboxFields.forEach((el: string) => {
        const coverageMarcked = dataComponents.coverages.find(
          (coverage) =>
            coverage.codigo ===
            stepThreeHomeQuotationFormCoverages.getFieldValue(el)?.codePlan
        );
        if (coverageMarcked) {
          quotationCoverages.push({
            riesgo: coverageMarcked.codigoRiesgo,
            cobertura: coverageMarcked.codigo,
            sumaAsegurada: coverageMarcked.sumaAseguradaPorDefecto,
            pormilaje: '0',
            prima: '0',
          });
        }
      });
      return quotationCoverages;
    },
    [stepThreeHomeQuotationFormCoverages]
  );

  const generateFeaturesByTab = useCallback(
    (tabKey: number): Array<PropertyFeature> => {
      const planSelected = stepTwoForm?.getFieldValue(`plan-tab-${tabKey}`);
      
      const features =
        allFeatures?.find(
          (feature: any) => String(feature.plan) === String(planSelected)
        )?.features ?? [];
      
      const featuresByTab: Array<PropertyFeature> = features.map(
        (feature: any) => ({
          aplica: feature.aplicar ? DECISION_TYPE.YES : DECISION_TYPE.NO,
          codigo: feature.codigo,
          codigoEquivalente: feature.codigoEquivalente,
          descripcion: feature.descripcion,
          incluyeWeb: DECISION_TYPE.YES,
          tiene: stepTwoForm?.getFieldValue(
            `feature-tab-${tabKey}-${feature.codigo}`
          )
            ? DECISION_TYPE.YES
            : DECISION_TYPE.NO,
        })
      );

      return featuresByTab;
    },
    [allFeatures, stepTwoForm]
  );

  const generateHomeComponentsInfo =
    useCallback((): Array<HomeQuotationComponent> => {
      let components: Array<HomeQuotationComponent> = [];

      tabs.forEach(({ key }: any) => {
        const location: string = stepTwoForm?.getFieldValue(
          `location-tab-${key}`
        );
        const provinceCode: string = stepTwoForm?.getFieldValue(
          `province-tab-${key}`
        );

        const localitiesOfProvince = allLocalitiesPerProvince.find(
          (el) => el.provinceCode === provinceCode
        )?.localities;

        const riskZone =
          localitiesOfProvince?.find(
            (el) => `${el.sufijoCodigoPostal}${el.codigoPostal}` === location
          )?.zonaRiesgosVarios ?? '';

        const featuresByTab: Array<PropertyFeature> = generateFeaturesByTab(key);
          
        const component: HomeQuotationComponent = {
          nroComponente: key,
          codPlan: stepTwoForm?.getFieldValue(`plan-tab-${key}`) as number,
          tipoVivienda: stepTwoForm?.getFieldValue(`type-housing-tab-${key}`), //* es 0 para polizas nuevas.
          caracteristicasBien: featuresByTab,
          cantCaracteristicas: featuresByTab.length,
          sufijoCodigoPostal: location[0],
          codigoPostal: location.slice(1),
          zonaRiesgo: riskZone,
          buenResultado: 0, //* es 0 para polizas nuevas.
          cobertura: generateCoveragesInfo(key),
          psua: 0, //* es 0 para polizas nuevas.
          capTarifa: '0',
          capTarifaInc1: '',
          capTarifaInc2: '',
          clasificacionRiesgo: 'A',
        };

        components.push(component);
      });

      setHomeQuotationComponents(components);

      return components;
    }, [
      tabs,
      allLocalitiesPerProvince,
      stepTwoForm,
      generateCoveragesInfo,
      setHomeQuotationComponents,
      generateFeaturesByTab,
    ]);

  const generateHomeComponentsInfoByPlan = useCallback(
    (dataComponents: { plan: string; coverages: Array<CoveragePerPlan> }) => {
      let components: Array<HomeQuotationComponent> = [];

      tabs.forEach(({ key }: any) => {
        const location: string = stepTwoForm?.getFieldValue(
          `location-tab-${key}`
        );
        const provinceCode: string = stepTwoForm?.getFieldValue(
          `province-tab-${key}`
        );

        const localitiesOfProvince = allLocalitiesPerProvince.find(
          (el) => el.provinceCode === provinceCode
        )?.localities;

        const riskZone =
          localitiesOfProvince?.find(
            (el) => `${el.sufijoCodigoPostal}${el.codigoPostal}` === location
          )?.zonaRiesgosVarios ?? '';

        const featuresByTab: Array<PropertyFeature> =
          generateFeaturesByTab(key);

        const component: HomeQuotationComponent = {
          nroComponente: key,
          codPlan: Number(dataComponents?.plan),
          tipoVivienda: 1, //* es 0 para polizas nuevas.
          caracteristicasBien: featuresByTab,
          cantCaracteristicas: 0,
          sufijoCodigoPostal: location[0],
          codigoPostal: location.slice(1),
          zonaRiesgo: riskZone,
          buenResultado: 0, //* es 0 para polizas nuevas.
          cobertura: generateCoveragesInfoByPlan(dataComponents),
          psua: 0, //* es 0 para polizas nuevas.
          capTarifa: '0',
          capTarifaInc1: '',
          capTarifaInc2: '',
          clasificacionRiesgo: 'A',
          
        };
        components.push(component);
      });

      setHomeQuotationComponents(components);

      return components;
    },
    [
      allLocalitiesPerProvince,
      generateCoveragesInfoByPlan,
      stepTwoForm,
      tabs,
      generateFeaturesByTab,
      setHomeQuotationComponents,
    ]
  );

  const generateHomeQuotationForm = useCallback((): HomeQuotationRequest => {
    const administrativeSurcharge = homePaymentMethodsForm.getFieldInstance('extraVariable') ? homePaymentMethodsForm?.getFieldValue('extraVariable') : commertialConditions?.porcentajeExtraPrimaVariable;
    const paymentMethodCode = homePaymentMethodsForm.getFieldInstance('paymentMethod') ? homePaymentMethodsForm?.getFieldValue('paymentMethod').split('-')[1] : getValueDefaultPaymentMethod(paymentMethodsList);
    const components = generateHomeComponentsInfo();
    
    return {
      token: quotationInfo?.token!,
      arse: quotationInfo?.arse!,
      rama: quotationInfo?.branch!,
      codigoTipoFormaPago: paymentMethodCode,
      cantComponentes: components.length,
      cliente: clientSelected!,
      componentes: components,
      recargoAdministrativo: administrativeSurcharge,
    };
  }, [
    quotationInfo,
    clientSelected,
    generateHomeComponentsInfo,
    homePaymentMethodsForm,
    commertialConditions,
    paymentMethodsList,
  ]);

  const generateHomeQuotationFormByPlan = useCallback(
    (plan: string) => {
      const administrativeSurcharge = homePaymentMethodsForm.getFieldInstance(
        'extraVariable'
      )
        ? homePaymentMethodsForm?.getFieldValue('extraVariable')
        : commertialConditions?.porcentajeExtraPrimaVariable;
      const paymentMethodCode = homePaymentMethodsForm.getFieldInstance(
        'paymentMethod'
      )
        ? homePaymentMethodsForm?.getFieldValue('paymentMethod').split('-')[1]
        : getValueDefaultPaymentMethod(paymentMethodsList);
      const filteredCoverage: {
        plan: string;
        coverages: Array<CoveragePerPlan>;
      } = allCoveragesPerPlan?.find(
        (el) => String(el.plan) === String(plan)
      ) as any;
      const componentsByPlan =
        generateHomeComponentsInfoByPlan(filteredCoverage);

      return {
        token: quotationInfo?.token!,
        arse: quotationInfo?.arse!,
        rama: quotationInfo?.branch!,
        codigoTipoFormaPago: paymentMethodCode,
        cantComponentes: componentsByPlan.length,
        cliente: clientSelected!,
        componentes: componentsByPlan,
        recargoAdministrativo: administrativeSurcharge,
      };
    },
    [
      clientSelected,
      commertialConditions?.porcentajeExtraPrimaVariable,
      generateHomeComponentsInfoByPlan,
      homePaymentMethodsForm,
      quotationInfo?.arse,
      quotationInfo?.branch,
      quotationInfo?.token,
      paymentMethodsList,
      allCoveragesPerPlan,
    ]
  );

  const handlerOnClickEmit = useCallback(async () => {
    setCurrentStep(QuotationStep.FOUR);
  }, [setCurrentStep]);

  const handlerOnClickQuotationClosed = useCallback(async () => {
    setCanShowSpinner(true);

    try {
      await stepThreeHomeQuotationFormCoverages.validateFields();

      const responses: Array<{
        planSelected: string;
        response: WebHomeQuotation;
      }> = [];

      for (const plan of plansSelected as Array<string>) {
        const formByPlan = generateHomeQuotationFormByPlan(plan);

        const response = await QuotationService.postGenerateHomeQuotation(
          formByPlan
        );

        updateFieldsBeforeQuotation(response.data.cotizacionWebHogar);

        responses.push({
          planSelected: plan,
          response: response.data.cotizacionWebHogar,
        });
      }

      if (responses.length > 0) {
        setLabelButton(QUOTATION_BUTTON.COTIZAR);
        setQuotationResponseClosed(responses);
      }
    } catch (error) {
    } finally {
      setCanShowSpinner(false);
    }
  }, [
    plansSelected,
    setCanShowSpinner,
    setLabelButton,
    setQuotationResponseClosed,
    stepThreeHomeQuotationFormCoverages,
    updateFieldsBeforeQuotation,
    generateHomeQuotationFormByPlan,
  ]);

  const handleQuotationByPlanType = useCallback(async () => {
    
    try {
      if (planTypeSelected === PLAN_TYPE.OPEN) {
        const form: HomeQuotationRequest = generateHomeQuotationForm();
        
        await handlerOnClickQuotation(form);

        return;
      }

      await handlerOnClickQuotationClosed();
    } catch (error) {}
  }, [
    generateHomeQuotationForm,
    handlerOnClickQuotation,
    handlerOnClickQuotationClosed,
    planTypeSelected,
  ]);

  const handlerOnClick = useCallback(async () => {
    try {
      if (labelButton === QUOTATION_BUTTON.EMITIR) {
        await handlerOnClickEmit();
      }

      if (
        labelButton === QUOTATION_BUTTON.COTIZAR ||
        labelButton === QUOTATION_BUTTON.RECOTIZAR
      ) {
        await handleQuotationByPlanType();
      }
    } catch (error) {}
  }, [labelButton, handlerOnClickEmit, handleQuotationByPlanType]);

  const executeQuotationForSelectedClosePlan = useCallback(async () => {
    setCanShowSpinner(true);

    try {
      const formByPlan = generateHomeQuotationFormByPlan(planSelectedSummary);
      
      const response = await QuotationService.postGenerateHomeQuotation(
        formByPlan
      );

      if (response.data) {
        //recover ID from original coverate and set it into response
        response.data.cotizacionWebHogar.componentes.forEach(
          ({ nroDeComponente, coberturas }) => {
            const planSelected =
              stepThreeHomeQuotationFormCoverages.getFieldValue(
                `plan-tab-${nroDeComponente}`
              );

            const coveragesPerPlan =
              allCoveragesPerPlan.find(
                (el) => (el.plan as string) === String(planSelected)
              )?.coverages ?? [];

            coberturas?.forEach((coverage) => {
              const originalCoverage = coveragesPerPlan.find(
                (item) =>
                  item.codigo === coverage.codigoCobertura &&
                  item.codigoRiesgo === coverage.codigoRiesgo
              );
              coverage.id = originalCoverage?.id;
            });
          }
        );

        updateFieldsBeforeQuotation(response.data.cotizacionWebHogar);
        scrollToEmitButton();
        setLabelButton(QUOTATION_BUTTON.EMITIR);

        const filteredResponses = quotationResponseClosed.filter(
          (el) => String(el.planSelected) !== String(planSelectedSummary)
        );

        setQuotationResponseClosed([
          ...filteredResponses,
          {
            planSelected: planSelectedSummary,
            response: response.data.cotizacionWebHogar,
          },
        ]);
      }
    } catch (error) {
    } finally {
      setCanShowSpinner(false);
    }
  }, [
    setCanShowSpinner,
    setLabelButton,
    planSelectedSummary,
    updateFieldsBeforeQuotation,
    generateHomeQuotationFormByPlan,
    setQuotationResponseClosed,
  ]);

  const fillStepThreeServices = useCallback(async () => {
    try {
      createTabs();
      await getCommertialsConditions();
    } catch (error) {}
  }, [createTabs, getCommertialsConditions]);

  const fillStepThreeData = useCallback(async (isRenewal: boolean) => {
    
    let quotationToProccess: LoadSavedQuotations;
    if(isRenewal){
      quotationToProccess = quotationToRenewal;
    }else{
      quotationToProccess = savedQuotation;
    }
    
    const {
      bienes = [],
      formaDePago,
      tipoDePlan,
    } = quotationToProccess;

    //* Se setea vacio ya que no tenemos la primera respuesta de la COTIZACIÓN en un retome.
    setQuotationResponse({
      sumaAsegurada: '',
      premio: '',
      prima: '',
      componentes: bienes.map(({ numeroComponente, caracteristicasSeleccionadas }) => {
        return {
          nroDeComponente: String(numeroComponente),
          producto: '',
          tipoVivienda: '',
          codigoPostal: '',
          sufijoCodigoPostal: '',
          zonaRiesgo: '',
          buenResultado: '',
          sumaAsegurada: '',
          premio: '',
          requiereInspeccion: '',
          porcentajeSumaAsegurada: '',
          capituloTarifa: '',
          capituloTarifaInc1: '',
          agravamiento: '',
          caracteristicas: isRenewal ? caracteristicasSeleccionadas : savedQuotation.caracteristicasSeleccionadas,
          coberturas: [],
          impuestos: [
            {
              codigoCobertura: '',
              impComision: '',
              impExtraPrimaFija: '',
              impImpuestosInternos: '',
              impIva: '',
              impIvaPercepcion: '',
              impIvaRnl: '',
              impIibbPercepcion: '',
              impIibbRiesgo: '',
              impIibbEmpresa: '',
              porImpuestosInternos: '',
              porIva: '',
              porIvaRnl: '',
              porIvaPercepcion: '',
              porTasaSsn: '',
              porServiciosSociales: '',
              impExtraPrimaVar: '',
              impRecargoFinanciero: '',
              selladoDeEmpresa: '',
              selladoDelRiesgo: '',
              impServiciosSociales: '',
              impTasaSsn: '',
              porComision: '',
              porExtraPrimaVar: '',
              porRecargoFinanciero: '',
              calculoImpuestos: '',
            },
          ],
          bonificaciones: [],
        };
      }),
      impuestos: {
        rama: '1',
        cantPolizaXRama: '',
        prima: '',
        porRecargoFinanciero: '',
        impRecargoFinanciero: '',
        impExtraPrimaFija: '',
        impSubtotalPrima: '',
        selladoDelRiesgo: '',
        selladoDeEmpresa: '',
        porImpuestosInternos: '',
        impImpuestosInternos: '',
        porServiciosSociales: '',
        impServiciosSociales: '',
        porTasaSsn: '',
        impTasaSsn: '',
        impIibbPercepcion: '',
        impIibbEmpresa: '',
        impIibbRiesgo: '',
        impIvaRnl: '',
        porIvaRnl: '',
        impIva: '',
        porIva: '',
      },
      condicionesComerciales: {
        cantPolizaXRama: '',
        porRecargoAdminist: '',
        impRecargoAdminist: '',
        porComisionProd: '',
        impComision: '',
        porRecargoAdminist1: '',
        porComisionProd1: '',
      },
      condicionesComerciales1: {
        rama: '1',
        porcRecargoAdminist: '',
      },
    });
    
    bienes.forEach(
      ({ coberturasSeleccionadas = [], planSeleccionado }, index: number) => {
        const _tab = index + 1;
        
        if (tipoDePlan === PLAN_TYPE.CLOSED) {
          const planField = `plan-tab-${_tab}`;
          stepThreeHomeQuotationFormCoverages.setFieldValue(
            planField,
            String(planSeleccionado)
          );
        }

        coberturasSeleccionadas.forEach(
          ({ codigo, codigoRiesgo, prima, sumaAsegurada }) => {
            if (tipoDePlan === PLAN_TYPE.OPEN) {
              const checkboxName = `checkbox_${codigo}-${codigoRiesgo}_home-tab-${_tab}`;
              const primaName = `prima_${codigo}-${codigoRiesgo}_home-tab-${_tab}`;
              const sumaAseguradaName = `sumaAsegurada_${codigo}-${codigoRiesgo}_home-tab-${_tab}`;

              stepThreeHomeQuotationForm.setFields([
                { name: checkboxName, value: true },
                { name: primaName, value: prima },
                { name: sumaAseguradaName, value: sumaAsegurada },
              ]);
            } else {
              const checkboxName = `aditional-plan-${planSeleccionado}-${codigo}`;
              stepThreeHomeQuotationFormCoverages.setFieldValue(
                checkboxName,
                true
              );
            }
          }
        );
      }
    );

    homePaymentMethodsForm.setFields([
      { name: `paymentMethod`, value: `${quotationToProccess.formaDePago.codigo}-${quotationToProccess.formaDePago.codigoDePlanDePagos}` },
      { name: `commission`, value: formaDePago.porcentajeComision },
      {
        name: `extraVariable`,
        value: formaDePago.porcentajeExtraPrimaVariable,
      },
      {
        name: `variation`,
        value:
          Number(formaDePago.porcentajeComision) -
          Number(commertialConditions!.porcentajeDeComision),
      },
    ]);

    setLabelButton(QUOTATION_BUTTON.RECOTIZAR);

    if(isRenewal){
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLED_RENEWAL
      )
    }else{
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLED
      );
    }
  }, [
    savedQuotation,
    quotationToRenewal,
    homePaymentMethodsForm,
    setLabelButton,
    setQuotationResponse,
    stepThreeHomeQuotationForm,
    setLoadSaveQuotationState,
    createTabs,
    stepThreeHomeQuotationFormCoverages,
    commertialConditions,
  ]);

  const setPlanClosedFromSavedQuotation = useCallback((isRenewal: boolean) => {
    
    let quotationToProccess: LoadSavedQuotations;
    if(isRenewal){
      quotationToProccess = quotationToRenewal;
    }else{
      quotationToProccess = savedQuotation;
    }
    const { bienes } = quotationToProccess;

    if (bienes.length) {
      const planSelected = String(bienes[0].planSeleccionado);
      setPlanSelectedSummary(planSelected);
      setPlansSelected([planSelected]);
    }
  }, [savedQuotation, quotationToRenewal, setPlanSelectedSummary, setPlansSelected]);

  const resetAllFormFields = useCallback(() => {
    stepThreeHomeQuotationForm.resetFields();
    stepThreeHomeQuotationFormCoverages.resetFields();
    stepThreeHomeQuotationFormSummary.resetFields();
  }, [
    stepThreeHomeQuotationForm,
    stepThreeHomeQuotationFormCoverages,
    stepThreeHomeQuotationFormSummary,
  ]);

  useEffect(() => {
    if (removedTabIndexes.length && currentStep === QuotationStep.THREE && formValues) {
      updateFormsAfterRemoveTabs(removedTabIndexes, [stepThreeHomeQuotationForm], formValues);
      setRemovedTabIndexes([]);
    };
  }, [removedTabIndexes, currentStep, formValues]);

  useEffect(() => {
    if (quotationResponse || quotationResponseClosed.length) {
      setIsPlanTypeADisabled(true);
      setIsPlanTypeCDisabled(true);
    }
  }, [quotationResponse, quotationResponseClosed]);

  useEffect(() => {
    if (currentStep === QuotationStep.THREE && !loadSaveQuotationState)
      getCommertialsConditions();
    /* eslint-disable-next-line */
  }, [currentStep, loadSaveQuotationState]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
      LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLED
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADING
      );
      fillStepThreeServices();
    }
    /* eslint-disable-next-line */
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
        LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADING &&
      commertialConditions &&
      quotationTabs.length === tabs.length
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADED
      );
    }
    /* eslint-disable-next-line */
  }, [loadSaveQuotationState, commertialConditions, quotationTabs, tabs]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
      LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADED
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLING
      );
      fillStepThreeData(false);
    }
    /* eslint-disable-next-line */
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (
      loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.FINISH_LOADING
    ) {
      if (planTypeSelected === PLAN_TYPE.OPEN) handlerOnClick();
      else setPlanClosedFromSavedQuotation(false);
    }
    /* eslint-disable-next-line */
  }, [loadSaveQuotationState]);

/**
 * Renewal
 */

useEffect(() => {
  if (currentStep === QuotationStep.THREE && !renewalQuotationState)
    getCommertialsConditions();
  /* eslint-disable-next-line */
}, [currentStep, renewalQuotationState]);

useEffect(() => {
  if (
    renewalQuotationState ===
    LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLED_RENEWAL
  ) {
    setRenewalQuotationState(
      LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADING_RENEWAL
    );
    fillStepThreeServices();
  }
  /* eslint-disable-next-line */
}, [renewalQuotationState]);

useEffect(() => {
  if (
    renewalQuotationState ===
      LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADING_RENEWAL &&
    commertialConditions &&
    quotationTabs.length === tabs.length
  ) {
    setRenewalQuotationState(
      LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADED_RENEWAL
    );
  }
  /* eslint-disable-next-line */
}, [renewalQuotationState, commertialConditions, quotationTabs, tabs]);

useEffect(() => {
  if (
    renewalQuotationState ===
    LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADED_RENEWAL
  ) {
    setRenewalQuotationState(
      LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLING_RENEWAL
    );
    fillStepThreeData(true);
  }
  /* eslint-disable-next-line */
}, [renewalQuotationState]);

useEffect(() => {
  if (
    renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.FINISH_LOADING_RENEWAL
  ) {
    if (planTypeSelected === PLAN_TYPE.OPEN) handlerOnClick();
    else setPlanClosedFromSavedQuotation(true);
  }
  /* eslint-disable-next-line */
}, [renewalQuotationState]);

/**
 * End renewal
 */

  useEffect(() => {
    if (currentStep === QuotationStep.THREE && quotationTabs.length) {
      setActiveTab(quotationTabs[0].key);
    }
  }, [currentStep, quotationTabs]);

  const values = useMemo(
    () => ({
      canGetCoveragesPerPlanByTab,
      clearPrimaFieldsOnFormChange,
      commertialConditions,
      generateHomeQuotationForm,
      generateHomeQuotationFormByPlan,
      homePaymentMethodsForm,
      homeQuotationComponents,
      isLoadingForm,
      labelButton,
      listPlansSelected,
      planSelectedSummary,
      quotationResponse,
      quotationResponseClosed,
      setCanGetCoveragesPerPlanByTab,
      setIsLoadingForm,
      setLabelButton,
      setListPlansSelected,
      setPlanSelectedSummary,
      setQuotationResponse,
      stepThreeHomeQuotationFormCoverages,
      stepThreeHomeQuotationFormSummary,
      handlerOnClickQuotation,
      updateFieldsBeforeQuotation,
      handlerOnClick,
      quotationTabs,
      activeTab,
      handlePressTab,
      stepThreeHomeQuotationForm,
      executeQuotationForSelectedClosePlan,
      resetAllFormFields,
      createTabs,
    }),
    [
      canGetCoveragesPerPlanByTab,
      clearPrimaFieldsOnFormChange,
      commertialConditions,
      generateHomeQuotationForm,
      generateHomeQuotationFormByPlan,
      homePaymentMethodsForm,
      homeQuotationComponents,
      isLoadingForm,
      labelButton,
      listPlansSelected,
      planSelectedSummary,
      quotationResponse,
      quotationResponseClosed,
      setCanGetCoveragesPerPlanByTab,
      setIsLoadingForm,
      setLabelButton,
      setListPlansSelected,
      setPlanSelectedSummary,
      setQuotationResponse,
      stepThreeHomeQuotationFormCoverages,
      stepThreeHomeQuotationFormSummary,
      handlerOnClickQuotation,
      updateFieldsBeforeQuotation,
      handlerOnClick,
      quotationTabs,
      activeTab,
      handlePressTab,
      stepThreeHomeQuotationForm,
      executeQuotationForSelectedClosePlan,
      resetAllFormFields,
      createTabs,
    ]
  );

  return (
    <StepThreeFormContext.Provider value={values}>
      {children}
    </StepThreeFormContext.Provider>
  );
};
